import { createApi } from "@reduxjs/toolkit/query/react";
import { publicBaseQuery } from "@/api/base/public/publicBaseQuery";

/**
 * Generic reader for the public reference lists a registration form feeds its selects from.
 *
 * A form schema names the endpoint by PATH ONLY, e.g. "astana-daryn/regions.json"; this slice
 * turns it into GET {API_BASE}/api/astana-daryn/regions.json. Keeping it generic is what lets
 * the backend add a new admin-configured form without any front-end change — a new select just
 * points at a new path.
 *
 * Response shapes accepted: {entities: [...]} (the platform envelope) or a bare array.
 *
 * The path is untrusted input (it arrives inside the invite's form_data), so it is validated:
 * relative, no scheme, no host, no traversal.
 */

export type ReferenceRow = Record<string, unknown>;

const SAFE_PATH = /^[a-z0-9][a-z0-9\-_/.]*$/i;

export function isSafeReferencePath(path: string): boolean {
  const p = path.trim();
  if (!p || p.length > 200) return false;
  if (p.startsWith("/") || p.includes("//") || p.includes("..")) return false;
  if (p.includes(":")) return false;
  return SAFE_PATH.test(p);
}

type EntitiesEnvelope = { entities?: ReferenceRow[] };

export const registerReferencePublicApi = createApi({
  reducerPath: "registerReferencePublicApi",
  baseQuery: publicBaseQuery(),
  endpoints: (builder) => ({
    getRegisterReference: builder.query<
      ReferenceRow[],
      { path: string; params?: Record<string, string> }
    >({
      query: ({ path, params }) => ({
        url: `/api/${path.trim()}`,
        method: "GET",
        ...(params && Object.keys(params).length > 0 ? { params } : {}),
      }),
      transformResponse: (response: EntitiesEnvelope | ReferenceRow[]) =>
        Array.isArray(response) ? response : (response?.entities ?? []),
    }),
  }),
});

export const { useGetRegisterReferenceQuery } = registerReferencePublicApi;
